import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  Email as EmailIcon,
  Event as CalendarIcon,
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import { useAuth } from '../contexts/AuthContext';

interface SyncStatus {
  google_connected: boolean;
  microsoft_connected: boolean;
  google_token_expires: string | null;
  microsoft_token_expires: string | null;
  recent_email_activities: number;
  recent_meeting_activities?: number;
  last_sync: string | null;
}

interface SyncResult {
  provider: string;
  total_messages?: number;
  total_events?: number;
  processed_messages?: number;
  processed_events?: number;
  new_activities: number;
  messages?: Array<{
    id: string;
    subject: string;
    sender?: string;
    created_activity: boolean;
  }>;
  events?: Array<{
    id: string;
    subject: string;
    start: string;
    created_activity: boolean;
  }>;
}

interface UpcomingMeeting {
  id: string;
  subject: string;
  contact_name: string;
  contact_email: string;
  scheduled_time: string;
  attendees: string;
}

export default function EmailCalendarIntegrationPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [syncDialog, setSyncDialog] = useState<{ open: boolean; type: 'email' | 'calendar' | null }>({
    open: false,
    type: null
  });
  const queryClient = useQueryClient();

  // Fetch email sync status
  const { data: emailStatus, isLoading: emailStatusLoading, error: emailStatusError } = useQuery<SyncStatus>({
    queryKey: ['emailSyncStatus'],
    queryFn: () => baseClient.get('/email/sync/status'),
    enabled: !!user,
    retry: false
  });

  // Fetch calendar sync status
  const { data: calendarStatus, isLoading: calendarStatusLoading, error: calendarStatusError } = useQuery<SyncStatus>({
    queryKey: ['calendarSyncStatus'],
    queryFn: () => baseClient.get('/calendar/sync/status'),
    enabled: !!user,
    retry: false
  });

  // Fetch upcoming meetings
  const { data: upcomingMeetings, isLoading: meetingsLoading, error: meetingsError } = useQuery<{
    upcoming_meetings: UpcomingMeeting[];
    total_meetings: number;
  }>({
    queryKey: ['upcomingMeetings'],
    queryFn: () => baseClient.get('/calendar/upcoming-meetings?days_ahead=7'),
    enabled: !!user,
    retry: false
  });

  // Email sync mutations
  const syncGoogleEmailMutation = useMutation({
    mutationFn: () => baseClient.post('/email/sync/google?limit=50'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSyncStatus'] });
      setSyncDialog({ open: false, type: null });
    }
  });

  const syncMicrosoftEmailMutation = useMutation({
    mutationFn: () => baseClient.post('/email/sync/microsoft?limit=50'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailSyncStatus'] });
      setSyncDialog({ open: false, type: null });
    }
  });

  // Calendar sync mutations
  const syncGoogleCalendarMutation = useMutation({
    mutationFn: () => baseClient.post('/calendar/sync/google?days_ahead=30'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarSyncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['upcomingMeetings'] });
      setSyncDialog({ open: false, type: null });
    }
  });

  const syncMicrosoftCalendarMutation = useMutation({
    mutationFn: () => baseClient.post('/calendar/sync/microsoft?days_ahead=30'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarSyncStatus'] });
      queryClient.invalidateQueries({ queryKey: ['upcomingMeetings'] });
      setSyncDialog({ open: false, type: null });
    }
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSync = (provider: 'google' | 'microsoft', type: 'email' | 'calendar') => {
    if (type === 'email') {
      if (provider === 'google') {
        syncGoogleEmailMutation.mutate();
      } else {
        syncMicrosoftEmailMutation.mutate();
      }
    } else {
      if (provider === 'google') {
        syncGoogleCalendarMutation.mutate();
      } else {
        syncMicrosoftCalendarMutation.mutate();
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getConnectionStatus = (connected: boolean, expires: string | null) => {
    if (!connected) {
      return { status: 'disconnected', color: 'error' as const, text: 'Not Connected' };
    }

    if (expires && new Date(expires) < new Date()) {
      return { status: 'expired', color: 'warning' as const, text: 'Token Expired' };
    }

    return { status: 'connected', color: 'success' as const, text: 'Connected' };
  };

  if (!user) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Required
          </Typography>
          <Typography>
            Please sign in to access email and calendar integration features.
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (emailStatusLoading || calendarStatusLoading || meetingsLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            <EmailIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Email & Calendar Integration
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Sync your emails and calendar events to automatically track customer interactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<HelpIcon />}
            href="/help#email-config"
            target="_blank"
            color="secondary"
          >
            Email Setup Help
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => window.open('/integrations', '_blank')}
          >
            Manage Connections
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Email Sync" icon={<EmailIcon />} />
          <Tab label="Calendar Sync" icon={<CalendarIcon />} />
          <Tab label="Upcoming Meetings" icon={<ScheduleIcon />} />
        </Tabs>
      </Box>

      {/* Email Sync Tab */}
      {activeTab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              📧 Quick Email Setup Guide
            </Typography>
            <Typography variant="body2">
              <strong>Gmail:</strong> Use smtp.gmail.com:587, enable 2FA and create an App Password<br />
              <strong>Outlook:</strong> Use smtp-mail.outlook.com:587 with your regular credentials<br />
              <strong>Exchange:</strong> Contact your IT department for server settings<br />
              <Button size="small" href="/help#email-config" target="_blank" sx={{ mt: 1 }}>
                View Detailed Setup Guide
              </Button>
            </Typography>
          </Alert>
          <Grid container spacing={3}>
            {/* Connection Status */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <GoogleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Google Gmail
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={getConnectionStatus(emailStatus?.google_connected || false, emailStatus?.google_token_expires || null).text}
                      color={getConnectionStatus(emailStatus?.google_connected || false, emailStatus?.google_token_expires || null).color}
                      icon={emailStatus?.google_connected ? <CheckIcon /> : <ErrorIcon />}
                    />
                  </Box>
                  {emailStatus?.google_connected && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Token expires: {emailStatus.google_token_expires ? formatDate(emailStatus.google_token_expires) : 'Unknown'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Recent activities: {emailStatus.recent_email_activities || 0}
                      </Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    onClick={() => handleSync('google', 'email')}
                    disabled={!emailStatus?.google_connected || syncGoogleEmailMutation.isPending}
                    fullWidth
                  >
                    {syncGoogleEmailMutation.isPending ? 'Syncing...' : 'Sync Gmail'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <MicrosoftIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Microsoft Outlook
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={getConnectionStatus(emailStatus?.microsoft_connected || false, emailStatus?.microsoft_token_expires || null).text}
                      color={getConnectionStatus(emailStatus?.microsoft_connected || false, emailStatus?.microsoft_token_expires || null).color}
                      icon={emailStatus?.microsoft_connected ? <CheckIcon /> : <ErrorIcon />}
                    />
                  </Box>
                  {emailStatus?.microsoft_connected && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Token expires: {emailStatus.microsoft_token_expires ? formatDate(emailStatus.microsoft_token_expires) : 'Unknown'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Recent activities: {emailStatus.recent_email_activities || 0}
                      </Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon />}
                    onClick={() => handleSync('microsoft', 'email')}
                    disabled={!emailStatus?.microsoft_connected || syncMicrosoftEmailMutation.isPending}
                    fullWidth
                  >
                    {syncMicrosoftEmailMutation.isPending ? 'Syncing...' : 'Sync Outlook'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Sync Status */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Email Sync Status
                  </Typography>
                  {emailStatus?.last_sync ? (
                    <Alert severity="success">
                      Last sync: {formatDate(emailStatus.last_sync)}
                    </Alert>
                  ) : (
                    <Alert severity="info">
                      No recent email sync activity. Connect your email accounts and run a sync to get started.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Calendar Sync Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* Connection Status */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <GoogleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Google Calendar
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={getConnectionStatus(calendarStatus?.google_connected || false, calendarStatus?.google_token_expires || null).text}
                    color={getConnectionStatus(calendarStatus?.google_connected || false, calendarStatus?.google_token_expires || null).color}
                    icon={calendarStatus?.google_connected ? <CheckIcon /> : <ErrorIcon />}
                  />
                </Box>
                {calendarStatus?.google_connected && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Token expires: {calendarStatus.google_token_expires ? formatDate(calendarStatus.google_token_expires) : 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Recent meetings: {calendarStatus.recent_meeting_activities || 0}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={() => handleSync('google', 'calendar')}
                  disabled={!calendarStatus?.google_connected || syncGoogleCalendarMutation.isPending}
                  fullWidth
                >
                  {syncGoogleCalendarMutation.isPending ? 'Syncing...' : 'Sync Google Calendar'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <MicrosoftIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Microsoft Calendar
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={getConnectionStatus(calendarStatus?.microsoft_connected || false, calendarStatus?.microsoft_token_expires || null).text}
                    color={getConnectionStatus(calendarStatus?.microsoft_connected || false, calendarStatus?.microsoft_token_expires || null).color}
                    icon={calendarStatus?.microsoft_connected ? <CheckIcon /> : <ErrorIcon />}
                  />
                </Box>
                {calendarStatus?.microsoft_connected && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Token expires: {calendarStatus.microsoft_token_expires ? formatDate(calendarStatus.microsoft_token_expires) : 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Recent meetings: {calendarStatus.recent_meeting_activities || 0}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={() => handleSync('microsoft', 'calendar')}
                  disabled={!calendarStatus?.microsoft_connected || syncMicrosoftCalendarMutation.isPending}
                  fullWidth
                >
                  {syncMicrosoftCalendarMutation.isPending ? 'Syncing...' : 'Sync Outlook Calendar'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Upcoming Meetings Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Upcoming Meetings (Next 7 Days)
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => queryClient.invalidateQueries({ queryKey: ['upcomingMeetings'] })}
                    disabled={meetingsLoading}
                  >
                    Refresh
                  </Button>
                </Box>

                {upcomingMeetings && upcomingMeetings.upcoming_meetings.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Subject</TableCell>
                          <TableCell>Contact</TableCell>
                          <TableCell>Scheduled Time</TableCell>
                          <TableCell>Attendees</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {upcomingMeetings.upcoming_meetings.map((meeting) => (
                          <TableRow key={meeting.id}>
                            <TableCell>{meeting.subject}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                                {meeting.contact_name}
                              </Box>
                            </TableCell>
                            <TableCell>{formatDate(meeting.scheduled_time)}</TableCell>
                            <TableCell>{meeting.attendees || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    No upcoming meetings found. Sync your calendar to see upcoming meetings with your contacts.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
